/**
 * Deliveries Actions
 */

import { groupBy } from 'lodash';
import Constants from '../../constants';

const {
  Deliveries,
  Delivery,
  Location,
  LocationHour,
  Locations,
  PurchaseOrders,
  Vendor,
  Block,
} = Constants;

/**
 * Action object dispatched to create a block
 * @param {object} block block data
 */
const createBlock = (block, cb) => ({
  type: Block.CREATE_BLOCK,
  block,
  cb,
});

/**
 * Action object dispatched when unable to create a block
 * @param {object} error error object
 */
const createBlockError = (error) => ({
  type: Block.CREATE_BLOCK_ERROR,
  error,
});

/**
 * Action object dispatched when create a block is successful
 */
const createBlockSuccess = ({ bookings }) => {
  const { ORDER_DELIVERY: deliveries, BLOCK: blocks } = groupBy(
    bookings,
    'type',
  );
  return {
    type: Block.CREATE_BLOCK_SUCCESS,
    deliveries,
    blocks,
  };
};

/**
 * Action object dispatched to create delivery
 */
const createDelivery = (delivery, purchaseOrderNumbers, cb) => ({
  type: Delivery.CREATE_DELIVERY,
  delivery,
  purchaseOrderNumbers,
  cb,
});

/**
 * Action object dispatched when unable to create delivery
 *
 * @param {object} error error object
 */
const createDeliveryError = (error) => ({
  type: Delivery.CREATE_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when delivery is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const createDeliverySuccess = ({ delivery }) => ({
  type: Delivery.CREATE_DELIVERY_SUCCESS,
  delivery,
});

/**
 * Action object dispatched to create location hour
 */
const createLocationHour = (locationId, hour) => ({
  type: LocationHour.CREATE_LOCATION_HOUR,
  locationId,
  hour,
});

/**
 * Action object dispatched when unable to create location hour
 *
 * @param {object} error error object
 */
const createLocationHourError = (error) => ({
  type: LocationHour.CREATE_LOCATION_HOUR_ERROR,
  error,
});

/**
 * Action object dispatched when location hour is created successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const createLocationHourSuccess = ({ hour, location }) => ({
  type: LocationHour.CREATE_LOCATION_HOUR_SUCCESS,
  hour,
  location,
});

/**
 * Action object dispatched to create a delivery
 *
 * @param {string} userId the user id
 * @param {object} delivery the data for the delivery
 * @param {array} purchaseOrderNumbers the purchase order ids to be delivered
 */
const createVendorDelivery = (userId, delivery, purchaseOrderNumbers) => ({
  type: Vendor.CREATE_VENDOR_DELIVERY,
  userId,
  delivery,
  purchaseOrderNumbers,
});

/**
 * Action object dispatched when unable to create delivery
 *
 * @param {object} error error object
 */
const createVendorDeliveryError = (error) => ({
  type: Vendor.CREATE_VENDOR_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when delivery created successfully
 *
 * @param {object} delivery delivery object
 */
const createVendorDeliverySuccess = ({ delivery }) => ({
  type: Vendor.CREATE_VENDOR_DELIVERY_SUCCESS,
  delivery,
});

/**
 * Action object dispatched to delete location hour
 */
const deleteLocationHour = (locationId, hourId) => ({
  type: LocationHour.DELETE_LOCATION_HOUR,
  locationId,
  hourId,
});

/**
 * Action object dispatched when unable to delete location hour
 *
 * @param {object} error error object
 */
const deleteLocationHourError = (error) => ({
  type: LocationHour.DELETE_LOCATION_HOUR_ERROR,
  error,
});

/**
 * Action object dispatched when location hour is deleted successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const deleteLocationHourSuccess = ({ hourId, locationId }) => ({
  type: LocationHour.DELETE_LOCATION_HOUR_SUCCESS,
  hourId,
  locationId,
});

/**
 * Action object dispatched to delete block
 */
const removeBlock = (blockId) => ({
  type: Block.REMOVE_BLOCK,
  blockId,
});

/**
 * Action object dispatched when unable to delete block
 */
const removeBlockError = (error) => ({
  type: Block.REMOVE_BLOCK_ERROR,
  error,
});

/**
 * Action object dispatched when succeeds to delete block
 */
const removeBlockSuccess = (blockId) => ({
  type: Block.REMOVE_BLOCK_SUCCESS,
  blockId,
});

/**
 * Action object dispatched to get block
 */
const getBlock = (blockId) => ({
  type: Block.GET_BLOCK,
  blockId,
});

/**
 * Action object dispatched when unable to get block
 */
const getBlockError = (error) => ({
  type: Block.GET_BLOCK_ERROR,
  error,
});

/**
 * Action object dispatched when succeed to get block
 */
const getBlockSuccess = ({ block }) => ({
  type: Block.GET_BLOCK_SUCCESS,
  block,
});

/**
 * Action object dispatched to get block
 */
const getBlocks = (options) => ({
  type: Block.GET_BLOCKS,
  options,
});

/**
 * Action object dispatched when unable to get block
 */
const getBlocksError = (error) => ({
  type: Block.GET_BLOCKS_ERROR,
  error,
});

/**
 * Action object dispatched when succeed to get block
 */
const getBlocksSuccess = ({ blocks }) => ({
  type: Block.GET_BLOCKS_SUCCESS,
  blocks,
});

/**
 * Action object dispatched to get delivery
 */
const getDelivery = (deliveryId) => ({
  type: Delivery.GET_DELIVERY,
  deliveryId,
});

/**
 * Action object dispatched when unable to get delivery
 *
 * @param {object} error error object
 */
const getDeliveryError = (error) => ({
  type: Delivery.GET_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when delivery is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const getDeliverySuccess = ({ delivery }) => ({
  type: Delivery.GET_DELIVERY_SUCCESS,
  delivery,
});

/**
 * Action object dispatched to get deliveries
 */
const getDeliveries = (options) => ({
  type: Deliveries.GET_DELIVERIES,
  options,
});

/**
 * Action object dispatched when unable to get deliveries
 *
 * @param {object} error error object
 */
const getDeliveriesError = (error) => ({
  type: Deliveries.GET_DELIVERIES_ERROR,
  error,
});

/**
 * Action object dispatched when deliveries is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const getDeliveriesSuccess = ({ bookings }) => {
  const { ORDER_DELIVERY: deliveries, BLOCK: blocks } = groupBy(
    bookings,
    'type',
  );

  return {
    type: Deliveries.GET_DELIVERIES_SUCCESS,
    deliveries,
    blocks,
  };
};

/**
 * Action object dispatched to get location hour
 */
const getLocationHour = (locationId, hourId) => ({
  type: LocationHour.GET_LOCATION_HOUR,
  locationId,
  hourId,
});

/**
 * Action object dispatched when unable to get location hour
 *
 * @param {object} error error object
 */
const getLocationHourError = (error) => ({
  type: LocationHour.GET_LOCATION_HOUR_ERROR,
  error,
});

/**
 * Action object dispatched when location hour is get successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const getLocationHourSuccess = ({ hour, location }) => ({
  type: LocationHour.GET_LOCATION_HOUR_SUCCESS,
  hour,
  location,
});

/**
 *
 * @param {string} userId the user id
 */
const getVendorDeliveries = (userId, params) => ({
  type: Vendor.GET_VENDOR_DELIVERIES,
  userId,
  params,
});

/**
 * Action object dispatched when unable to fetch deliveries
 *
 * @param {object} error error object
 */
const getVendorDeliveriesError = (error) => ({
  type: Vendor.GET_VENDOR_DELIVERIES_ERROR,
  error,
});

/**
 * Action object dispatched when fetching deliveries succeeds
 *
 * @param {array} deliveries deliveries array
 */
const getVendorDeliveriesSuccess = ({ deliveries, count }) => ({
  type: Vendor.GET_VENDOR_DELIVERIES_SUCCESS,
  deliveries,
  count,
});

/**
 * Action object dispatched to get a delivery
 *
 * @param {string} userId the user id
 * @param {string} deliveryId the delivery id
 */
const getVendorDelivery = (userId, deliveryId) => ({
  type: Vendor.GET_VENDOR_DELIVERY,
  userId,
  deliveryId,
});

/**
 * Action object dispatched when unable to fetch the delivery
 *
 * @param {object} error error object
 */
const getVendorDeliveryError = (error) => ({
  type: Vendor.GET_VENDOR_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when fetching the delivery succeeds
 *
 * @param {object} delivery delivery object
 */
const getVendorDeliverySuccess = ({ delivery }) => ({
  type: Vendor.GET_VENDOR_DELIVERY_SUCCESS,
  delivery,
});

/**
 * Action object dispatched to remove delivery
 *
 * @param {string} deliveryId - the delivery id
 * for the delivery to delete.
 */
const removeDelivery = (deliveryId, reason) => ({
  type: Delivery.REMOVE_DELIVERY,
  deliveryId,
  reason,
});

/**
 * Action object dispatched when unable to remove delivery
 *
 * @param {object} error error object
 */
const removeDeliveryError = (error) => ({
  type: Delivery.REMOVE_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when delivery is removed successfully
 */
const removeDeliverySuccess = (deliveryId) => ({
  type: Delivery.REMOVE_DELIVERY_SUCCESS,
  deliveryId,
});

/**
 * Action object dispatched to remove vendor delivery
 *
 * @param {string} userId - the user id
 * @param {string} deliveryId - the delivery id
 * for the delivery to delete.
 */
const removeVendorDelivery = (userId, deliveryId, reason) => ({
  type: Vendor.REMOVE_VENDOR_DELIVERY,
  userId,
  deliveryId,
  reason,
});

/**
 * Action object dispatched when unable to remove vendor delivery
 *
 * @param {object} error error object
 */
const removeVendorDeliveryError = (error) => ({
  type: Vendor.REMOVE_VENDOR_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when vendor delivery is removed successfully
 */
const removeVendorDeliverySuccess = (deliveryId) => ({
  type: Vendor.REMOVE_VENDOR_DELIVERY_SUCCESS,
  deliveryId,
});

/**
 * Action object dispatched to update block
 */
const updateBlock = (blockId, block, cb) => ({
  type: Block.UPDATE_BLOCK,
  blockId,
  block,
  cb,
});

/**
 * Action object dispatched when unable to update block
 */
const updateBlockError = (error) => ({
  type: Block.UPDATE_BLOCK_ERROR,
  error,
});

/**
 * Action object dispatched when succeeds to update block
 */
const updateBlockSuccess = ({ bookings }) => {
  const { ORDER_DELIVERY: deliveries, BLOCK: blocks } = groupBy(
    bookings,
    'type',
  );

  return {
    type: Block.UPDATE_BLOCK_SUCCESS,
    deliveries,
    blocks,
  };
};

/**
 * Action object dispatched to update delivery
 */
const updateDelivery = (deliveryId, delivery, purchaseOrderNumbers, cb) => ({
  type: Delivery.UPDATE_DELIVERY,
  deliveryId,
  delivery,
  purchaseOrderNumbers,
  cb,
});

/**
 * Action object dispatched to check in delivery
 */
const checkInDelivery = (deliveryId, deliveredAt, cb) => ({
  type: Delivery.CHECK_IN_DELIVERY,
  deliveryId,
  deliveredAt,
  cb,
});

/**
 * Action object dispatched when unable to update delivery
 *
 * @param {object} error error object
 */
const updateDeliveryError = (error) => ({
  type: Delivery.UPDATE_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when delivery is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const updateDeliverySuccess = ({ delivery }) => ({
  type: Delivery.UPDATE_DELIVERY_SUCCESS,
  delivery,
});

/**
 * Action object dispatched when updating a delivery
 *
 * @param {string} userId the user id
 * @param {string} deliveryId the id of the delivery to be updated
 * @param {object} update the data to be updated
 * @param {array} purchaseOrderNumbers the purchase order numbers to be delivered
 */
const updateVendorDelivery = (
  userId,
  deliveryId,
  update,
  purchaseOrderNumbers,
) => ({
  type: Vendor.UPDATE_VENDOR_DELIVERY,
  userId,
  deliveryId,
  update,
  purchaseOrderNumbers,
});

/**
 * Action object dispatched when updating delivery fails
 *
 * @param {object} error error object
 */
const updateVendorDeliveryError = (error) => ({
  type: Vendor.UPDATE_VENDOR_DELIVERY_ERROR,
  error,
});

/**
 * Action object dispatched when update of delivery is successful
 */
const updateVendorDeliverySuccess = ({ delivery }) => ({
  type: Vendor.UPDATE_VENDOR_DELIVERY_SUCCESS,
  delivery,
});

/**
 * Action object dispatched to update location hour
 */
const updateLocationHour = (locationId, hourId, hour) => ({
  type: LocationHour.UPDATE_LOCATION_HOUR,
  locationId,
  hourId,
  hour,
});

/**
 * Action object dispatched when unable to update location hour
 *
 * @param {object} error error object
 */
const updateLocationHourError = (error) => ({
  type: LocationHour.UPDATE_LOCATION_HOUR_ERROR,
  error,
});

/**
 * Action object dispatched when location hour is update successfully
 *
 * @param {object} action:
 * {
 *   @property {object} delivery: object containing delivery data
 * }
 */
const updateLocationHourSuccess = ({ hour, location }) => ({
  type: LocationHour.UPDATE_LOCATION_HOUR_SUCCESS,
  hour,
  location,
});

/**
 * Action objects when gets deliverable purchase orders dispatched
 *
 * @param id
 * @param locationId
 * @returns {{type: string, userId: string}}
 */
const getDeliverablePurchaseOrders = (
  id,
  locationId,
  isVendorId = false,
  params,
) => ({
  type: PurchaseOrders.GET_DELIVERABLE_PURCHASE_ORDERS,
  isVendorId,
  locationId,
  id,
  params,
});

/**
 * Action objects when gets deliverable purchase orders errors
 *
 * @param error
 * @returns {{type: string, error: *}}
 */
const getDeliverablePurchaseOrdersError = (error) => ({
  type: PurchaseOrders.GET_DELIVERABLE_PURCHASE_ORDERS_ERROR,
  error,
});

/**
 * Action objects when gets deliverable purchase orders succeeds
 *
 * @param purchaseOrders
 * @returns {{type: string, purchaseOrders: <PurchaseOrder>}}
 */
const getDeliverablePurchaseOrdersSuccess = ({ purchaseOrders }) => ({
  type: PurchaseOrders.GET_DELIVERABLE_PURCHASE_ORDERS_SUCCESS,
  purchaseOrders,
});

/**
 * Action object dispatched to get availabilities
 *
 * @param {string} deliveryId - the delivery id
 * for the delivery to delete.
 */
const getAvailabilities = (options) => ({
  type: Delivery.GET_AVAILABILITIES,
  options,
});

/**
 * Action object dispatched when unable to get availabilities
 *
 * @param {object} error error object
 */
const getAvailabilitiesError = (error) => ({
  type: Delivery.GET_AVAILABILITIES_ERROR,
  error,
});

/**
 * Action object dispatched when
 * availabilities are successfully fetched
 *
 * @param {<object>} availabilities
 */
const getAvailabilitiesSuccess = ({ availabilities }) => ({
  type: Delivery.GET_AVAILABILITIES_SUCCESS,
  availabilities,
});

/**
 * Action object dispatched to create location
 */
const createLocation = (location) => ({
  type: Location.CREATE_LOCATION,
  location,
});

/**
 * Action object dispatched when unable to create location
 *
 * @param {object} error error object
 */
const createLocationError = (error) => ({
  type: Location.CREATE_LOCATION_ERROR,
  error,
});

/**
 * Action object dispatched when location is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} location: object containing location data
 * }
 */
const createLocationSuccess = ({ location }) => ({
  type: Location.CREATE_LOCATION_SUCCESS,
  location,
});

/**
 * Action object dispatched to get location
 */
const getLocation = (locationId) => ({
  type: Location.GET_LOCATION,
  locationId,
});

/**
 * Action object dispatched when unable to get location
 *
 * @param {object} error error object
 */
const getLocationError = (error) => ({
  type: Location.GET_LOCATION_ERROR,
  error,
});

/**
 * Action object dispatched when location is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} location: object containing location data
 * }
 */
const getLocationSuccess = ({ location }) => ({
  type: Location.GET_LOCATION_SUCCESS,
  location,
});

/**
 * Action object dispatched to get locations
 */
const getLocations = () => ({
  type: Locations.GET_LOCATIONS,
});

/**
 * Action object dispatched when unable to get locations
 *
 * @param {object} error error object
 */
const getLocationsError = (error) => ({
  type: Locations.GET_LOCATIONS_ERROR,
  error,
});

/**
 * Action object dispatched when locations is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} location: object containing location data
 * }
 */
const getLocationsSuccess = ({ locations }) => ({
  type: Locations.GET_LOCATIONS_SUCCESS,
  locations,
});

/**
 * Action object dispatched to update location
 */
const updateLocation = (locationId, location) => ({
  type: Location.UPDATE_LOCATION,
  locationId,
  location,
});

/**
 * Action object dispatched when unable to update location
 *
 * @param {object} error error object
 */
const updateLocationError = (error) => ({
  type: Location.UPDATE_LOCATION_ERROR,
  error,
});

/**
 * Action object dispatched when location is fetched successfully
 *
 * @param {object} action:
 * {
 *   @property {object} location: object containing location data
 * }
 */
const updateLocationSuccess = ({ location }) => ({
  type: Location.UPDATE_LOCATION_SUCCESS,
  location,
});

/**
 * Action object dispatched to remove location
 *
 * @param {string} locationId - the location id
 * for the location to delete.
 */
const removeLocation = (locationId) => ({
  type: Location.REMOVE_LOCATION,
  locationId,
});

/**
 * Action object dispatched when unable to remove location
 *
 * @param {object} error error object
 */
const removeLocationError = (error) => ({
  type: Location.REMOVE_LOCATION_ERROR,
  error,
});

/**
 * Action object dispatched when location is removed successfully
 */
const removeLocationSuccess = () => ({
  type: Location.REMOVE_LOCATION_SUCCESS,
});

export default {
  createBlock,
  createBlockError,
  createBlockSuccess,
  createDelivery,
  createDeliveryError,
  createDeliverySuccess,
  createLocationHour,
  createLocationHourError,
  createLocationHourSuccess,
  removeBlock,
  removeBlockError,
  removeBlockSuccess,
  getBlock,
  getBlockError,
  getBlockSuccess,
  getBlocks,
  getBlocksError,
  getBlocksSuccess,
  getDelivery,
  getDeliveryError,
  getDeliverySuccess,
  getDeliveries,
  getDeliveriesError,
  getDeliveriesSuccess,
  getLocationHour,
  getLocationHourError,
  getLocationHourSuccess,
  deleteLocationHour,
  deleteLocationHourError,
  deleteLocationHourSuccess,
  updateBlock,
  updateBlockError,
  updateBlockSuccess,
  updateDelivery,
  updateDeliveryError,
  updateDeliverySuccess,
  updateLocationHour,
  updateLocationHourError,
  updateLocationHourSuccess,
  removeDelivery,
  removeDeliveryError,
  removeDeliverySuccess,
  getDeliverablePurchaseOrders,
  getDeliverablePurchaseOrdersError,
  getDeliverablePurchaseOrdersSuccess,
  getAvailabilities,
  getAvailabilitiesError,
  getAvailabilitiesSuccess,
  createLocation,
  createLocationError,
  createLocationSuccess,
  getLocation,
  getLocationError,
  getLocationSuccess,
  getLocations,
  getLocationsError,
  getLocationsSuccess,
  updateLocation,
  updateLocationError,
  updateLocationSuccess,
  removeLocation,
  removeLocationError,
  removeLocationSuccess,
  createVendorDelivery,
  createVendorDeliveryError,
  createVendorDeliverySuccess,
  removeVendorDelivery,
  removeVendorDeliveryError,
  removeVendorDeliverySuccess,
  getVendorDeliveries,
  getVendorDeliveriesError,
  getVendorDeliveriesSuccess,
  getVendorDelivery,
  getVendorDeliveryError,
  getVendorDeliverySuccess,
  updateVendorDelivery,
  updateVendorDeliveryError,
  updateVendorDeliverySuccess,
  checkInDelivery,
};
